import { useApolloClient } from '@apollo/client'
import { Box, Button, Typography } from '@mui/material'
import {
  AssetType,
  DispositionData,
  dispositionSchema,
} from 'common/disposition'
import {
  UndocumentedScan,
  ScanState,
  SelectedScan,
  AssetData,
  BatchAddAssetsToSurgeryResponse,
  MediaAction,
  ManualInputFormData,
} from 'common/types'
import { ListEmptyState } from 'components/ListEmptyState'
import { UndocumentedScanCard } from 'components/UndocumentedScanCard'
import { GET_SURGERY_QUERY } from 'lib/apollo/schema'
import { useUndocumentedAssetsContext } from 'lib/context/UndocumentedAssetsContext'
import _ from 'lodash'
import { useState, useEffect, useCallback } from 'react'
import { parseAssetIdentifiers } from 'lib/utils/ParseAssetIdentifiers/parseAssetIdentifiers'
import { UndocumentedListLogicProps } from './UndocumentedList.types'
import { useRfSpongeCount } from 'lib/context/RfSpongeCountContext/RfSpongeCountContext'
import { BatchDispositionModalLogicProps } from 'components/BatchDispositionModal/BatchDispositionModal.types'
import { useUser } from 'app/User'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { enqueueSnackbar } from 'notistack'
import { useLocation } from 'react-router-dom'
import { useDeleteUndocumentedScanMutation } from 'lib/apollo/hooks'
import { AddMissingFieldsProps } from 'components/organisms/StoredAsset/StoredAsset.types'

export const useUndocumentedListLogic = ({
  surgeryId,
  batchAddAssetsToSurgery,
}: UndocumentedListLogicProps) => {
  const state = useLocation().state as {
    selectedScanId: string[]
  }

  const stateScanIdToRemove = state?.selectedScanId || null

  const [deleteUndocumentedScan] = useDeleteUndocumentedScanMutation(surgeryId)

  const {
    undocumentedScans,
    setUndocumentedScans,
    setStopGetUndocumentedScansPolling,
  } = useUndocumentedAssetsContext()

  const deleteScansLocal = useCallback(
    (scanIds: string[]) => {
      setScanStateMap((prevStateMap) => {
        const newStateMap = { ...prevStateMap }

        scanIds.forEach((scanId) => {
          delete newStateMap[scanId]
        })

        return newStateMap
      })

      setUndocumentedScans((prevScans) =>
        prevScans.filter((scan) => !scanIds.includes(scan._id))
      )
    },
    [setUndocumentedScans]
  )

  useEffect(() => {
    if (stateScanIdToRemove) {
      deleteUndocumentedScan({ variables: { scanIds: stateScanIdToRemove } })
    }
  }, [stateScanIdToRemove, undocumentedScans])

  useEffect(() => {
    if (stateScanIdToRemove) deleteScansLocal(stateScanIdToRemove)
  }, [deleteScansLocal, stateScanIdToRemove])

  const flags = useFlags()
  const { isRep } = useUser()

  const repCanEdit = flags?.repCanEdit ?? false
  const hideDocumentSelected = isRep && !repCanEdit ? true : false

  // --------------------  Utils --------------------
  const client = useApolloClient()

  const { matchToAssetGroup, addAssetGroupsCount } = useRfSpongeCount()

  // --------------------  States --------------------
  const [selectedMultipackScan, setSelectedMultipackScan] =
    useState<ManualInputFormData | null>(null)

  const [uploadedMedia, setUploadedMedia] = useState<string[]>([])
  const [isDisposeOpen, setIsDisposeOpen] = useState(false)
  const [isAssociatedModalOpen, setIsAssociatedModalOpen] = useState(false)
  const [scanStateMap, setScanStateMap] = useState<Record<string, ScanState>>(
    (undocumentedScans || []).reduce(
      (acc: Record<string, ScanState>, scan: UndocumentedScan) => {
        acc[scan._id] = {
          isSelected: false,
          isAssociatedProduct: false,
          isMultiPack:
            scan.deviceCount > 1 && scan.assetType === 'non-biological',
        }
        return acc
      },
      {}
    )
  )
  const [addMissingFields, setAddMissingFields] =
    useState<AddMissingFieldsProps>({
      id: _.uniqueId(),
      open: false,
    })

  useEffect(() => {
    setScanStateMap((prev) =>
      (undocumentedScans || []).reduce(
        (acc: Record<string, ScanState>, scan: UndocumentedScan) => {
          acc[scan._id] = {
            isSelected: prev[scan._id]?.isSelected || false,
            isAssociatedProduct: false,
            isMultiPack:
              scan.deviceCount > 1 && scan.assetType === 'non-biological',
          }
          return acc
        },
        {}
      )
    )
  }, [undocumentedScans])

  const [selectedScanId, setSelectedScanId] = useState<(string | undefined)[]>(
    []
  )

  useEffect(() => {
    setStopGetUndocumentedScansPolling(isDisposeOpen)
  }, [isDisposeOpen, setStopGetUndocumentedScansPolling])

  useEffect(() => {
    setSelectedScanId(
      Object.keys(scanStateMap)
        .map((id) => {
          if (scanStateMap[id].isSelected === true) return id
          else return undefined
        })
        .filter((item) => item !== undefined)
    )
  }, [scanStateMap])

  // Array for storing the undocumentedScans that are coming from DTM
  const [dtmScrewAssets, setDTMScrewAssets] = useState<UndocumentedScan[]>([])
  const areThereDTMScrews = dtmScrewAssets.length > 0

  const filterDTMAssets = (isWasted: boolean) => {
    return dtmScrewAssets.filter((dtmScrewAsset) =>
      isWasted
        ? dtmScrewAsset.dtmScrewData?.wasted
        : !dtmScrewAsset.dtmScrewData?.wasted
    )
  }

  const areAllAssetsSelected = (assets: UndocumentedScan[]) => {
    if (assets.length === 0) {
      return false
    }

    const allAssetsAreFromDTM = assets.every((asset) => asset.isDTMScrew)

    if (assets[0].assetType === 'non-biological') {
      if (
        assets.filter((asset) => asset.deviceCount === 1).length === 0 &&
        !allAssetsAreFromDTM
      ) {
        return false
      }
      return assets
        .filter((asset) => asset.deviceCount === 1 || asset.isDTMScrew)
        .every((asset) =>
          Object.keys(scanStateMap).some(
            (scanId) => scanId === asset._id && scanStateMap[scanId].isSelected
          )
        )
    } else {
      return assets.every((asset) =>
        Object.keys(scanStateMap).some(
          (scanId) => scanId === asset._id && scanStateMap[scanId].isSelected
        )
      )
    }
  }

  const wastedDTMScrews = filterDTMAssets(true)
  const implantedDTMScrews = filterDTMAssets(false)
  const areAllDTMImplantedSelected = areAllAssetsSelected(implantedDTMScrews)
  const areAllDTMWastedSelected = areAllAssetsSelected(wastedDTMScrews)

  const multipackAssets = undocumentedScans.filter(
    (asset) =>
      asset.deviceCount > 1 &&
      asset.assetType === 'non-biological' &&
      !asset.isDTMScrew
  )

  // --------------------  Constants --------------------
  const scanMap = undocumentedScans?.reduce(
    (acc: Record<string, UndocumentedScan>, scan: UndocumentedScan) => {
      acc[scan._id] = scan
      return acc
    },
    {}
  )

  const selectedScans: SelectedScan[] = Object.entries(scanStateMap)
    .filter(([_, s]) => s?.isSelected)
    .map(([scanId, _]) => ({ ...scanMap[scanId], ...scanStateMap[scanId] }))

  const filterByAssetType = (
    scans: UndocumentedScan[],
    assetType: AssetType
  ) => {
    return scans?.filter(
      (scan: UndocumentedScan) => scan.assetType === assetType
    )
  }

  const areAllSelected = (
    assets: UndocumentedScan[],
    stateMap: Record<string, ScanState>
  ) => {
    if (assets[0]?.assetType === 'non-biological') {
      return (
        assets?.length > 0 &&
        assets
          .filter((asset) => asset.deviceCount === 1)
          .every((scan: UndocumentedScan) => stateMap[scan._id]?.isSelected)
      )
    } else {
      return (
        assets?.length > 0 &&
        assets.every((scan: UndocumentedScan) => stateMap[scan._id]?.isSelected)
      )
    }
  }

  const hardwareAssets = filterByAssetType(undocumentedScans, 'non-biological')
  const consumableAssets = filterByAssetType(undocumentedScans, 'consumable')
  const otherAssets = filterByAssetType(
    undocumentedScans,
    'other-non-biological'
  )
  const explantedAssets = filterByAssetType(undocumentedScans, 'explanted')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let associatedProducts = [] as UndocumentedScan[]
  undocumentedScans?.map((scan: UndocumentedScan) => {
    const { isAssociatedProduct } = parseAssetIdentifiers({
      deviceDescription: scan.deviceDescription,
      deviceCount: scan.deviceCount,
      idType: scan.secondaryDeviceIdType,
      assetType: scan.assetType,
      gmdnPTDefinition: scan.gmdnPTDefinition,
    })

    if (isAssociatedProduct) {
      associatedProducts.push(scan)
    }
    return associatedProducts
  })

  const isAssetTypeSelected = (assetType: AssetType) => {
    return undocumentedScans?.some(
      (scan: UndocumentedScan) =>
        scan.assetType === assetType && scanStateMap[scan._id]?.isSelected
    )
  }

  const isConsumableSelected = isAssetTypeSelected('consumable')
  const isHardwareSelected = isAssetTypeSelected('non-biological')
  const isOtherSelected = isAssetTypeSelected('other-non-biological')
  const isExplantedSelected = isAssetTypeSelected('explanted')

  const isAssociatedSelected = useCallback(() => {
    return associatedProducts.some(
      (scan: UndocumentedScan) => scanStateMap[scan._id]?.isAssociatedProduct
    )
  }, [associatedProducts, scanStateMap])
  const isNonAssociatedHardwareSelected = hardwareAssets?.some(
    (hardwareScan: UndocumentedScan) => {
      const { isAssociatedProduct } = parseAssetIdentifiers({
        deviceDescription: hardwareScan.deviceDescription,
        deviceCount: hardwareScan.deviceCount,
        idType: hardwareScan.secondaryDeviceIdType,
        assetType: hardwareScan.assetType,
        gmdnPTDefinition: hardwareScan.gmdnPTDefinition,
      })

      return scanStateMap[hardwareScan._id]?.isSelected && !isAssociatedProduct
    }
  )

  const selectedCount = selectedScans.length
  const isAllConsumableSelected = areAllSelected(consumableAssets, scanStateMap)
  const isAnySelected = selectedCount > 0

  // --------------------  Component Lifestyle Methods --------------------
  useEffect(() => {
    if (associatedProducts.length > 0) {
      associatedProducts.forEach((scan: UndocumentedScan) => {
        setScanStateMap((prevState) => ({
          ...prevState,
          [scan._id]: {
            ...prevState[scan._id],
            isSelected: !isRep ? true : false,
            isAssociatedProduct: true,
          },
        }))
      })
      setIsAssociatedModalOpen(!isRep ? true : false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Get all of the undocumented scans that came from the DTM usage workflow
  useEffect(() => {
    const getDTMScrewAssets = () => {
      const dtmAssets = undocumentedScans.filter((scan) => scan.isDTMScrew)

      setDTMScrewAssets(dtmAssets)
    }

    getDTMScrewAssets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undocumentedScans.length])

  // --------------------  Handles --------------------
  const toggleSelection = (scanId: string) => {
    const isCurrentMultiPack =
      scanStateMap[scanId]?.isMultiPack &&
      !undocumentedScans.find((scan) => scan._id === scanId)?.isDTMScrew
    const isCurrentSelected = scanStateMap[scanId]?.isSelected

    // Handle multipack selection
    if (isCurrentMultiPack) {
      if (isCurrentSelected) {
        // Deselect current multipack
        setSelectedMultipackScan(null)
      } else {
        // Select a new multipack
        const selectedMulti = undocumentedScans.find(
          (item) => item._id === scanId
        )
        setSelectedMultipackScan({
          assetType: 'non-biological',
          bidAssetId: selectedMulti?.bidAssetId,
          bidCompanyId: selectedMulti?.bidCompanyId,
          catalogNumber:
            selectedMulti?.catalogNumber ?? selectedMulti?.versionModelNumber,
          chargeable: selectedMulti?.chargeable,
          companyName: selectedMulti?.companyName ?? '',
          cost: selectedMulti?.cost,
          deviceCount: selectedMulti?.deviceCount ?? 1,
          deviceDescription: selectedMulti?.deviceDescription,
          deviceId: selectedMulti?.deviceId,
          expirationDate: selectedMulti?.expirationDate ?? '',
          fromAssetInstanceId: null,
          gmdnPTDefinition: selectedMulti?.gmdnPTDefinition,
          hasLotSerial: !!selectedMulti?.lotBatch,
          id: selectedMulti?.bidAssetId ?? 0,
          isManualAddition: selectedMulti?.isManualAddition,
          issuingAgency: selectedMulti?.issuingAgency,
          lotBatch: selectedMulti?.lotBatch,
          manufacturingDate: selectedMulti?.manufacturingDate,
          pkgQuantity: selectedMulti?.pkgQuantity,
          quantity: 1,
          secondaryDeviceId: selectedMulti?.secondaryDeviceId,
          secondaryDeviceIdPkgQuantity: null,
          secondaryDeviceIdPkgType: null,
          secondaryDeviceIdType: selectedMulti?.secondaryDeviceIdType,
          serialNumber: selectedMulti?.serialNumber,
          sizeString: selectedMulti?.sizeString,
          sizeText: selectedMulti?.sizeText,
          sourceId: null,
          udi: selectedMulti?.udi,
          versionModelNumber: selectedMulti?.versionModelNumber,
          companyId: selectedMulti?.bidCompanyId,
          count: selectedMulti?.deviceCount ?? 1,
          parentCompany: selectedMulti?.parentCompany || null,
          parentCompanyId: selectedMulti?.parentCompanyId || null,
        })
      }
    } else {
      // Clear multipack selection when selecting a normal product
      setSelectedMultipackScan(null)
    }

    // Update scan state map
    setScanStateMap((prev) => {
      const toggleSelected = !prev[scanId]?.isSelected
      const isDTM = undocumentedScans.find(
        (scan) => scan._id === scanId
      )?.isDTMScrew

      const stateMap = Object.keys(prev).reduce(
        (acc: Record<string, ScanState>, key) => {
          acc[key] = {
            ...prev[key],
            isSelected:
              key === scanId
                ? toggleSelected
                : prev[scanId]?.isMultiPack && !isDTM
                ? false
                : prev[key].isMultiPack && !isDTM
                ? false
                : prev[key].isSelected,
          }
          return acc
        },
        {}
      )

      return stateMap
    })
  }

  const handleToggleHardware = () => {
    setScanStateMap((s) => toggleAssetTypeSelection(s, 'non-biological'))
  }

  const handleToggleConsumable = () => {
    setScanStateMap((s) => toggleAssetTypeSelection(s, 'consumable'))
  }

  const handleToggleExplanted = () => {
    setScanStateMap((s) => toggleAssetTypeSelection(s, 'explanted'))
  }

  const handleToggleOther = () => {
    setScanStateMap((s) => toggleOtherSelection(s))
  }

  const handleToggleDTMHardware = (
    dispositionStatus: 'wasted' | 'implanted'
  ) => {
    setSelectedMultipackScan(null)
    setScanStateMap((s) =>
      toggleDTMSelection(s, 'non-biological', dispositionStatus)
    )
  }

  const submitDisposition = (
    data: DispositionData,
    assetGroupMatch?: AssetData
  ) => {
    let selected = [] as UndocumentedScan[]
    if (isAssociatedModalOpen) {
      selectedScans.filter((scan: UndocumentedScan) => {
        const isAssociatedProduct = associatedProducts.some(
          (associatedProduct: UndocumentedScan) =>
            associatedProduct._id === scan._id
        )
        if (isAssociatedProduct) {
          selected.push(scan)
        }
        return selected
      })
      const scans = selected.map((scan: UndocumentedScan) => {
        const { isRfMultipackConsumable, isSpongeConsumable } =
          parseAssetIdentifiers({
            assetType: scan.assetType,
            deviceCount: scan.deviceCount,
            deviceDescription: scan.deviceDescription,
            idType: scan.secondaryDeviceIdType,
          })
        return {
          scanId: scan._id,
          media: uploadedMedia,
          ..._.omit(scan, [
            'isSelected',
            'isExpireAccepted',
            'assetType',
            'user',
            'createdAt',
            'updatedAt',
            '__typename',
            '_id',
            'isAssociatedProduct',
          ]),
          isSponge: isRfMultipackConsumable || isSpongeConsumable,
          dtmScrewData: scan.dtmScrewData
            ? _.omit(scan.dtmScrewData, ['__typename'])
            : undefined,
        }
      })

      deleteScansLocal(selectedScans.map((scan: UndocumentedScan) => scan._id))
      const restructuredScans = scans.map((scan) => {
        const {
          isExplanted,
          originalAssetType,
          originalImplantSite,
          originalSiteLocation,
          ...rest
        } = scan
        return rest
      })

      batchAddAssetsToSurgery({
        variables: {
          disposition: _.omit(data, 'count'),
          scans: restructuredScans,
          deleteUndocumentedScansIds: selected.map(
            (scan: UndocumentedScan) => scan._id
          ),
        },
      })
        ?.then(
          (result: {
            data: { batchAddAssetsToSurgery: { addedScanIds: never[] } }
          }) => {
            setIsAssociatedModalOpen(false)

            if (isNonAssociatedHardwareSelected) {
              setIsDisposeOpen(true)
            }

            client.refetchQueries({
              include: [GET_SURGERY_QUERY],
            })
          }
        )
        .catch(() => {
          // NOTE: might want to handle this in the disposition modal
          enqueueSnackbar('Failed to document and add assets to procedure', {
            variant: 'error',
          })
        })
    } else {
      const scans = selectedScans.map((scan: UndocumentedScan) => {
        const { isRfMultipackConsumable, isSpongeConsumable } =
          parseAssetIdentifiers({
            assetType: scan.assetType,
            deviceCount: scan.deviceCount,
            deviceDescription: scan.deviceDescription,
            idType: scan.secondaryDeviceIdType,
          })
        return {
          scanId: scan._id,
          media: uploadedMedia,
          ..._.omit(scan, [
            'isSelected',
            'isExpireAccepted',
            'assetType',
            'user',
            'createdAt',
            'updatedAt',
            '__typename',
            '_id',
            'isAssociatedProduct',
          ]),
          isSponge: isRfMultipackConsumable || isSpongeConsumable,
          dtmScrewData: scan.dtmScrewData
            ? _.omit(scan.dtmScrewData, ['__typename'])
            : undefined,
        }
      })

      deleteScansLocal(selectedScans.map((scan: UndocumentedScan) => scan._id))

      const restructuredScans = scans.map((scan) => {
        const {
          isExplanted,
          originalAssetType,
          originalImplantSite,
          originalSiteLocation,
          ...rest
        } = scan
        return rest
      })

      batchAddAssetsToSurgery({
        variables: {
          disposition: _.omit(data, 'count'),
          scans: restructuredScans,
          deleteUndocumentedScansIds: selectedScans.map(
            (scan: UndocumentedScan) => scan._id
          ),
        },
      })
        .then((result: BatchAddAssetsToSurgeryResponse) => {
          if (isDisposeOpen) {
            setIsDisposeOpen(false)
          }

          if (assetGroupMatch && addAssetGroupsCount) {
            addAssetGroupsCount()
          }
          client.refetchQueries({
            include: [GET_SURGERY_QUERY],
          })
        })
        .catch(() => {
          // NOTE: might want to handle this in the disposition modal
          enqueueSnackbar('Failed to document and add assets to procedure', {
            variant: 'error',
          })
        })
        .finally(() => {
          setUploadedMedia([])
        })
    }
  }

  const handleConsumableSubmit = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault()

    const assetGroupMatch =
      (matchToAssetGroup && matchToAssetGroup(selectedScans)) || undefined

    submitDisposition(
      {
        assetType: 'consumable',
        assetTray: undefined,
        implantStatus: undefined,
      },
      assetGroupMatch
    )
  }

  const handleAssociatedSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const result = dispositionSchema.safeParse({
      assetTray: undefined,
      assetType: 'non-biological',
      implantStatus: 'ASSOCIATED_ASSET',
    })
    if (result.success) {
      submitDisposition(result.data)
    }
  }

  const toggleAssetTypeSelection = (
    stateMap: Record<string, ScanState>,
    assetType: string
  ) => {
    return Object.keys(stateMap).reduce((acc: Record<string, ScanState>, k) => {
      acc[k] = {
        ...stateMap[k],
        isSelected:
          scanMap[k]?.assetType === assetType && !stateMap[k].isMultiPack
            ? !areAllSelected(
                filterByAssetType(undocumentedScans, assetType),
                stateMap
              )
            : false,
      }
      return acc
    }, {})
  }

  const toggleDTMSelection = (
    stateMap: Record<string, ScanState>,
    assetType: string,
    dispositionStatus: 'wasted' | 'implanted'
  ) => {
    return Object.keys(stateMap).reduce((acc: Record<string, ScanState>, k) => {
      const dtmAsset = dtmScrewAssets.find((dtmAsset) => dtmAsset._id === k)
      const shouldBeWasted = dispositionStatus === 'wasted'

      acc[k] = {
        ...stateMap[k],
        isSelected:
          scanMap[k].assetType === assetType && dtmAsset
            ? !stateMap[k].isSelected
              ? shouldBeWasted
                ? dtmAsset.dtmScrewData?.wasted
                : !dtmAsset.dtmScrewData?.wasted
              : false
            : false,
      }

      return acc
    }, {})
  }

  const getDispositionStatus =
    (): BatchDispositionModalLogicProps['dispositionStatus'] => {
      if (areAllDTMWastedSelected) {
        return 'WASTED'
      }
      if (areAllDTMImplantedSelected && implantedDTMScrews.length > 0) {
        return 'IMPLANTED'
      }
    }

  const toggleOtherSelection = (stateMap: Record<string, ScanState>) => {
    return Object.keys(stateMap).reduce((acc: Record<string, ScanState>, k) => {
      acc[k] = {
        ...stateMap[k],
        isSelected:
          scanMap[k].assetType !== 'non-biological' &&
          scanMap[k].assetType !== 'consumable' &&
          scanMap[k].assetType !== 'explanted' &&
          !stateMap[k].isMultiPack
            ? !areAllSelected(
                filterByAssetType(undocumentedScans, 'other-non-biological'),
                stateMap
              )
            : false,
      }
      return acc
    }, {})
  }

  const handleAddMissingFields = (
    fields: string[],
    assetId?: AssetData['_id']
  ) => {
    setAddMissingFields({
      open: true,
      assetId,
      fields,
    })
  }

  const handleCloseMissingFieldsModal = () => {
    setAddMissingFields({
      open: false,
      id: _.uniqueId(),
    })
  }

  useEffect(() => {
    if (!addMissingFields.open) {
      setAddMissingFields({
        open: false,
        id: _.uniqueId(),
      })
    }
  }, [addMissingFields.open])

  const renderAssetCards = (
    assets: UndocumentedScan[],
    surgeryId: string,
    toggleSelection: (id: string) => void,
    isAssetTypeSelected: boolean
  ) =>
    assets.map((scan: UndocumentedScan) => {
      const isSelected = scanStateMap[scan._id]?.isSelected
      const isExpireAccepted = scanStateMap[scan._id]?.isExpireAccepted
      return (
        <UndocumentedScanCard
          renderDelete
          implantSite={scan.originalImplantSite}
          siteLocation={scan.originalSiteLocation}
          key={scan._id}
          isManualAddition={scan?.isManualAddition}
          dataTestId={scan._id}
          surgeryId={surgeryId}
          onClick={() => toggleSelection(scan._id)}
          scan={scan}
          isExpireAccepted={isExpireAccepted}
          cardStyles={{
            my: 1,
            display: 'block',
            borderColor: isSelected ? 'success.main' : 'grey.300',
            bgcolor: isSelected ? 'success.lightest' : 'white',
          }}
          disabled={isAssetTypeSelected}
          handleAddMissingFields={handleAddMissingFields}
        />
      )
    })

  const AssetSection = ({
    title,
    assets,
    toggle,
    isSelected,
    surgeryId,
    toggleSelection,
    isAssetTypeSelected,
    showSelection = true,
    titlePadding = 0,
  }: any) => (
    <Box width="100%" my={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          variant="h3"
          data-testid={`${title.toLowerCase()}-header`}
          sx={{ paddingLeft: titlePadding }}
        >
          {title}
        </Typography>
        {showSelection && (
          <Button
            data-testid={`toggle-${title.toLowerCase()}-button`}
            onClick={toggle}
            size="small"
            sx={{ mt: 0.25 }}
          >
            {isSelected ? 'Unselect All' : 'Select All'}
          </Button>
        )}
      </Box>
      {renderAssetCards(
        assets,
        surgeryId,
        toggleSelection,
        isAssetTypeSelected
      )}
    </Box>
  )

  const renderAssetBox = (
    title: string,
    assets: UndocumentedScan[],
    handleToggle: () => void,
    isAssetTypeSelected: boolean
  ) => {
    const isHardWare = title.includes('Implantable Hardware/Associated Product')
    const nonDTMAssetsNonMultipakAssets = assets.filter(
      (asset) =>
        (!asset.isDTMScrew && asset.deviceCount === 1) ||
        (asset.deviceCount > 1 && asset.assetType !== 'non-biological')
    )

    return (
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="h2"
            data-testid={`${title.toLowerCase()}-header`}
          >
            {title}
          </Typography>
          {!hideDocumentSelected &&
            (assets[0].assetType !== 'non-biological' ||
              assets.filter((asset) => asset.deviceCount === 1).length !==
                0) && (
              <Button
                data-testid={`toggle-${title.toLowerCase()}-button`}
                onClick={() => {
                  handleToggle()
                  setSelectedMultipackScan(null)
                }}
                size="small"
                sx={{ mt: 0.25 }}
              >
                {areAllAssetsSelected(assets) ? 'Unselect All' : 'Select All'}
              </Button>
            )}
        </Box>
        {renderAssetCards(
          nonDTMAssetsNonMultipakAssets,
          surgeryId,
          toggleSelection,
          isAssetTypeSelected
        )}
        {isHardWare && multipackAssets.length > 0 && (
          <AssetSection
            title="Multipack"
            titlePadding={3}
            showSelection={false}
            assets={multipackAssets}
            surgeryId={surgeryId}
            toggleSelection={toggleSelection}
            isAssetTypeSelected={isAssetTypeSelected}
          />
        )}
        {isHardWare && implantedDTMScrews.length > 0 && (
          <AssetSection
            title="DTM Implanted"
            assets={implantedDTMScrews}
            toggle={() => handleToggleDTMHardware('implanted')}
            isSelected={areAllDTMImplantedSelected}
            scanStateMap={scanStateMap}
            surgeryId={surgeryId}
            toggleSelection={toggleSelection}
            isAssetTypeSelected={isAssetTypeSelected}
          />
        )}
        {isHardWare && wastedDTMScrews.length > 0 && (
          <AssetSection
            title="DTM Wasted"
            assets={wastedDTMScrews}
            toggle={() => handleToggleDTMHardware('wasted')}
            isSelected={areAllDTMWastedSelected}
            scanStateMap={scanStateMap}
            surgeryId={surgeryId}
            toggleSelection={toggleSelection}
            isAssetTypeSelected={isAssetTypeSelected}
          />
        )}
      </Box>
    )
  }

  const renderEmptyState = (showEmptyState: boolean) => {
    if (showEmptyState) {
      return (
        <ListEmptyState
          containerStyles={{ my: 12 }}
          message={`Undocumented list is empty!\nProceed to Home page to capture a new product.\nNavigate to Documented list to view products ready for EMR submission.`}
        />
      )
    }
    return null
  }

  const handleSaveUploadedMedia = (media: string[], action: MediaAction) => {
    setUploadedMedia((prev) => {
      if (action === 'add') {
        return [...prev, ...media]
      } else if (action === 'delete') {
        return prev.filter((item) => !media.includes(item))
      }
      return prev
    })
  }

  return {
    isRep,
    renderAssetBox,
    renderEmptyState,
    isDisposeOpen,
    setIsDisposeOpen,
    selectedScans,
    hardwareAssets,
    consumableAssets,
    otherAssets,
    isConsumableSelected,
    isHardwareSelected,
    isOtherSelected,
    isAllConsumableSelected,
    isAnySelected,
    submitDisposition,
    handleToggleHardware,
    handleToggleConsumable,
    handleToggleOther,
    handleConsumableSubmit,
    undocumentedScans,
    isAssociatedModalOpen,
    setIsAssociatedModalOpen,
    toggleSelection,
    scanStateMap,
    setScanStateMap,
    associatedProducts,
    isAssociatedSelected,
    handleAssociatedSubmit,
    isNonAssociatedHardwareSelected,
    dtmScrewAssets,
    areAllDTMImplantedSelected,
    areAllDTMWastedSelected,
    handleSaveUploadedMedia,
    areThereDTMScrews,
    getDispositionStatus,
    hideDocumentSelected,
    selectedMultipackScan,
    selectedScanId,
    explantedAssets,
    handleToggleExplanted,
    isExplantedSelected,
    addMissingFields,
    handleCloseMissingFieldsModal,
  }
}
